import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import ProductTariffFieldError from 'Components/ProductTariffFieldErrors'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { WithLoader } from 'Components/WithLoader'
import {
  useCreateSingleTariffMutation,
  useUpdateSingleTariffMutation
} from 'Services/ProductManagement/Features/Tariff/tariffApi'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/types'
import { TariffStatus } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetVatDefinitionsQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import { TARIFF_CODE_LENGTH } from 'Util/constants'
import { formatToUTCISO, formatViolationErrorField } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@mui/material'
import type React from 'react'
import { useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  tariff_code: [],
  description: [],
  valid_from: [],
  valid_until: [],
  status: [],
  pre_order_start_timestamp: []
}

interface TariffFormProps {
  tariff?: Tariff
  successBaseUrl: string
}

const generateTariffValidityTitle = () => {
  return `tariff-validity-title`
}

export default function TariffForm({ tariff, successBaseUrl }: Readonly<TariffFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!tariff

  const [createSingleTariff, createStatus] = useCreateSingleTariffMutation()
  const [updateSingleTariff, updateStatus] = useUpdateSingleTariffMutation()
  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [tariffDetails, setTariffDetails] = useState<Partial<Tariff>>(isEdit ? tariff : {})

  const tariffValidity = tariffDetails?.active_period ?? null

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const { data: vatDefinitions = [], isFetching: isVatDefinitionsFetching } =
    useGetVatDefinitionsQuery()

  useEffect(() => {
    if (isEdit && tariff) {
      setTariffDetails(tariff)
    }
  }, [tariff, isEdit])

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Tariff
  ) => {
    if (value !== undefined) {
      setTariffDetails(prevState => ({ ...prevState, [field]: value }) as Tariff)
    }
  }

  const handleValidityDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    setTariffDetails(
      prevState =>
        ({
          ...prevState,
          active_period: {
            ...(prevState.active_period ?? null),
            [name]: formattedValue,
            title: generateTariffValidityTitle()
          }
        }) as Tariff
    )
  }

  const handlePreOrderStartTimestampChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    setTariffDetails(prevState => ({ ...prevState, [name]: formattedValue }) as Tariff)
  }

  const handleRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    setTariffDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const tariffData = {
      ...tariffDetails,
      product_ids: tariffDetails.product_ids ?? [],
      parent_product_ids: tariffDetails.parent_product_ids ?? []
    }

    const mutation = isEdit
      ? updateSingleTariff({
          ...tariffData,
          id: tariff.id
        })
      : createSingleTariff(tariffData)

    mutation
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Tariff successfully saved.`)
        navigate(successBaseUrl)
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations, message } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error saving the tariff.`)
        }
      })
  }

  function setValidationErrors(violations: ProductTariffViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const tariffStatusOptions = [
    {
      label: t`Active`,
      value: TariffStatus.ACTIVE
    },
    {
      label: t`Passive`,
      value: TariffStatus.PASSIVE
    },
    {
      label: t`Draft`,
      value: TariffStatus.DRAFT
    },
    {
      label: t`Production ready`,
      value: TariffStatus.STAGED
    },
    {
      label: t`Pre-sale`,
      value: TariffStatus.ACTIVE_PRE_ORDER
    }
  ]

  const tariffStatusLabelId = useId()

  return (
    <WithLoader loading={isVatDefinitionsFetching}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="tariff_code"
                required
                label={t`Tariff code`}
                size="small"
                value={tariffDetails?.tariff_code}
                slotProps={{
                  htmlInput: {
                    minLength: TARIFF_CODE_LENGTH,
                    maxLength: TARIFF_CODE_LENGTH
                  }
                }}
                error={fieldErrors.tariff_code.length > 0}
                onChange={e => handleChange(e.target.value, 'tariff_code')}
                helperText={fieldErrors.tariff_code.length > 0 ? fieldErrors.tariff_code[0] : ''}
              />
              <TextField
                name="description"
                required
                label={t`Tariff description`}
                size="small"
                sx={{ minWidth: 280 }}
                value={tariffDetails?.description}
                error={fieldErrors.description.length > 0}
                onChange={e => handleChange(e.target.value, 'description')}
                helperText={fieldErrors.description.length > 0 ? fieldErrors.description[0] : ''}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CustomDateTimePicker
                name="valid_from"
                value={tariffValidity ? new Date(tariffValidity.valid_from) : null}
                dateToCompare={tariffValidity ? new Date(tariffValidity.valid_until) : null}
                dateToCompareFieldName="valid_until"
                isStartDate={true}
                onChange={handleValidityDateTimePickerChange}
                label={t`Active from`}
                isError={fieldErrors.valid_from?.length > 0}
                isRequired={true}
                helperText={fieldErrors.valid_from.length > 0 ? fieldErrors.valid_from[0] : ''}
              />
              <Box>{` - `}</Box>
              <CustomDateTimePicker
                name="valid_until"
                value={tariffValidity ? new Date(tariffValidity.valid_until) : null}
                dateToCompare={tariffValidity ? new Date(tariffValidity.valid_from) : null}
                dateToCompareFieldName="valid_from"
                isStartDate={false}
                onChange={handleValidityDateTimePickerChange}
                label={t`Active to`}
                isError={fieldErrors.valid_until?.length > 0}
                isRequired={true}
                helperText={fieldErrors.valid_until.length > 0 ? fieldErrors.valid_until[0] : ''}
              />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <CustomDateTimePicker
                name="pre_order_start_timestamp"
                value={
                  tariffDetails?.pre_order_start_timestamp
                    ? new Date(tariffDetails.pre_order_start_timestamp)
                    : null
                }
                onChange={handlePreOrderStartTimestampChange}
                label={t`Pre-order start time`}
                isError={fieldErrors.pre_order_start_timestamp.length > 0}
                helperText={
                  fieldErrors.pre_order_start_timestamp.length > 0
                    ? fieldErrors.pre_order_start_timestamp[0]
                    : ''
                }
              />
            </Box>
            <Box>
              <FormControl sx={{ minWidth: 200 }} size="small" required>
                <InputLabel size="small">{t`VAT definition`}</InputLabel>
                <Select
                  name="vat_definition_id"
                  value={tariffDetails.vat_definition_id ?? ''}
                  label={t`VAT definition`}
                  size="small"
                  onChange={e => handleChange(e.target.value, 'vat_definition_id')}
                >
                  {vatDefinitions.map(vatDefinition => (
                    <MenuItem key={vatDefinition.id} value={vatDefinition.id}>
                      {vatDefinition.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControl required error={fieldErrors.status.length > 0}>
                <StyledLabel id={tariffStatusLabelId}>{t`Tariff status`}</StyledLabel>
                <RadioGroup
                  value={tariffDetails.status}
                  onChange={handleRadioButtonChange}
                  name="status"
                  aria-labelledby={tariffStatusLabelId}
                >
                  {tariffStatusOptions.map(({ label, value }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio size="small" required />}
                      label={label}
                    />
                  ))}
                </RadioGroup>
                <ProductTariffFieldError error={fieldErrors.status} identifier="status" />
              </FormControl>
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save tariff`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
